import { readEmbeddedGalleryItems } from './vehicle-gallery-api.js';
import { bindVehicleGalleryLightbox } from './vehicle-gallery-lightbox.js';

class ProductVehicleGallery extends HTMLElement {
  /** @type {import('@theme/vehicle-gallery-api').GalleryItem[]} */
  #items = [];

  connectedCallback() {
    const dataId = this.dataset.galleryDataId || 'product-vehicle-gallery-data';
    this.#items = readEmbeddedGalleryItems(dataId);
    this.#renderItems();
    bindVehicleGalleryLightbox(this);
    this.addEventListener('click', (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;

      const carouselControl = target.closest('[data-carousel-prev], [data-carousel-next], [data-carousel-dot]');
      if (!(carouselControl instanceof HTMLElement)) return;

      event.preventDefault();
      event.stopPropagation();
      const carousel = carouselControl.closest('[data-gallery-carousel]');
      if (carousel instanceof HTMLElement) {
        this.#handleCarouselClick(carousel, carouselControl);
      }
    });
  }

  #renderItems() {
    const grid = this.querySelector('[data-vehicle-gallery-grid]');
    if (!(grid instanceof HTMLElement)) return;

    const placeholderAssets = (this.dataset.placeholderAssets || '')
      .split('|')
      .map((asset) => asset.trim())
      .filter(Boolean);

    grid.innerHTML = this.#items
      .map((item, index) => this.#renderCard(item, index, placeholderAssets))
      .join('');
  }

  #normalizeImageUrl(url) {
    const value = String(url || '').trim();
    if (value.startsWith('//')) return `https:${value}`;
    return value;
  }

  /**
   * @param {import('@theme/vehicle-gallery-api').GalleryItem} item
   */
  #getItemImages(item) {
    if (item.images?.length) {
      return item.images.map((image) => ({
        ...image,
        url: this.#normalizeImageUrl(image.url),
      }));
    }
    if (item.imageUrl) {
      return [
        {
          url: this.#normalizeImageUrl(item.imageUrl),
          alt: item.imageAlt,
          width: item.imageWidth,
          height: item.imageHeight,
        },
      ];
    }
    return [];
  }

  /**
   * @param {import('@theme/vehicle-gallery-api').GalleryImage[]} images
   */
  #encodeImagesAttr(images) {
    return JSON.stringify(images).replace(/&/g, '&amp;').replace(/'/g, '&#39;').replace(/</g, '&lt;');
  }

  /**
   * @param {import('@theme/vehicle-gallery-api').GalleryItem} item
   * @param {string} placeholder
   */
  #renderMedia(item, placeholder) {
    const images = this.#getItemImages(item);

    if (images.length === 0) {
      return placeholder
        ? `<div class="vehicle-gallery__media"><img class="vehicle-gallery__image vehicle-gallery__image--placeholder" src="${this.#escape(placeholder)}" alt="${this.#escape(item.cardTitle)}" loading="lazy" width="900" height="675"></div>`
        : '<div class="vehicle-gallery__media"></div>';
    }

    if (images.length === 1) {
      const image = images[0];
      return `
        <div
          class="vehicle-gallery__media"
          data-gallery-lightbox
          data-carousel-index="0"
          data-carousel-images='${this.#encodeImagesAttr(images)}'
        >
          <img
            class="vehicle-gallery__image"
            src="${this.#escape(image.url)}"
            alt="${this.#escape(image.alt)}"
            loading="lazy"
            width="${image.width || 900}"
            height="${image.height || 675}"
          >
        </div>
      `;
    }

    const firstImage = images[0];
    const dots = images
      .map(
        (_, dotIndex) =>
          `<button type="button" class="vehicle-gallery__carousel-dot${dotIndex === 0 ? ' is-active' : ''}" data-carousel-dot="${dotIndex}" aria-label="Image ${dotIndex + 1} of ${images.length}"></button>`
      )
      .join('');

    return `
      <div
        class="vehicle-gallery__media vehicle-gallery__media--carousel"
        data-gallery-lightbox
        data-gallery-carousel
        data-carousel-index="0"
        data-carousel-images='${this.#encodeImagesAttr(images)}'
      >
        <img
          class="vehicle-gallery__image"
          data-carousel-active
          src="${this.#escape(firstImage.url)}"
          alt="${this.#escape(firstImage.alt)}"
          loading="lazy"
          width="${firstImage.width || 900}"
          height="${firstImage.height || 675}"
        >
        <button type="button" class="vehicle-gallery__carousel-btn vehicle-gallery__carousel-btn--prev" data-carousel-prev aria-label="Previous image">‹</button>
        <button type="button" class="vehicle-gallery__carousel-btn vehicle-gallery__carousel-btn--next" data-carousel-next aria-label="Next image">›</button>
        <div class="vehicle-gallery__carousel-dots" data-carousel-dots>${dots}</div>
      </div>
    `;
  }

  /** @param {HTMLElement} carousel @param {HTMLElement} control */
  #handleCarouselClick(carousel, control) {
    const images = this.#readCarouselImages(carousel);
    if (!images.length) return;

    const currentIndex = Number(carousel.dataset.carouselIndex) || 0;

    if (control.hasAttribute('data-carousel-prev')) {
      this.#setCarouselIndex(carousel, currentIndex - 1, images);
      return;
    }

    if (control.hasAttribute('data-carousel-next')) {
      this.#setCarouselIndex(carousel, currentIndex + 1, images);
      return;
    }

    if (control.hasAttribute('data-carousel-dot')) {
      this.#setCarouselIndex(carousel, Number(control.getAttribute('data-carousel-dot')) || 0, images);
    }
  }

  /** @param {HTMLElement} carousel */
  #readCarouselImages(carousel) {
    const raw = carousel.getAttribute('data-carousel-images');
    if (!raw) return [];

    try {
      const parsed = JSON.parse(raw.replace(/&amp;/g, '&').replace(/&#39;/g, "'").replace(/&lt;/g, '<'));
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  /**
   * @param {HTMLElement} carousel
   * @param {number} index
   * @param {import('@theme/vehicle-gallery-api').GalleryImage[]} images
   */
  #setCarouselIndex(carousel, index, images) {
    if (!images.length) return;

    const normalized = ((index % images.length) + images.length) % images.length;
    const image = images[normalized];
    carousel.dataset.carouselIndex = String(normalized);

    const activeImage = carousel.querySelector('[data-carousel-active]');
    if (activeImage instanceof HTMLImageElement) {
      activeImage.src = image.url;
      activeImage.alt = image.alt;
      if (image.width) activeImage.width = image.width;
      if (image.height) activeImage.height = image.height;
    }

    carousel.querySelectorAll('[data-carousel-dot]').forEach((dot, dotIndex) => {
      if (dot instanceof HTMLElement) {
        dot.classList.toggle('is-active', dotIndex === normalized);
      }
    });
  }

  /**
   * @param {import('@theme/vehicle-gallery-api').GalleryItem} item
   * @param {number} index
   * @param {string[]} placeholderAssets
   */
  #renderCard(item, index, placeholderAssets) {
    const placeholder =
      placeholderAssets[index % placeholderAssets.length] || placeholderAssets[0] || '';
    const mediaHtml = this.#renderMedia(item, placeholder);
    const metaHtml = `
      <div class="vehicle-gallery__meta">
        ${
          item.cardTitle
            ? `<button type="button" class="vehicle-gallery__vehicle vehicle-gallery__lightbox-trigger" data-gallery-lightbox>${this.#escape(item.cardTitle)}</button>`
            : ''
        }
        ${
          item.wheelLabel && item.cardUrl
            ? `<a class="vehicle-gallery__wheel" href="${this.#escape(item.cardUrl)}">${this.#escape(item.wheelLabel)}</a>`
            : item.wheelLabel
              ? `<p class="vehicle-gallery__wheel">${this.#escape(item.wheelLabel)}</p>`
              : ''
        }
      </div>
    `;

    const cardInner = `<div class="vehicle-gallery__card-body">${mediaHtml}${metaHtml}</div>`;

    return `
      <li class="vehicle-gallery__item">
        <div class="vehicle-gallery__card">
          ${cardInner}
        </div>
      </li>
    `;
  }

  /** @param {string} value */
  #escape(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }
}

if (!customElements.get('product-vehicle-gallery')) {
  customElements.define('product-vehicle-gallery', ProductVehicleGallery);
}
